/**
 * CheckpointsSection 供應商確認 (S3.2) — the three-checkpoint timeline
 * (A1 估計 after PO / A2 出貨通知再確認 / A3 裝櫃實際), the per-line
 * confirmed-bucket table (read from the OCC projections; variance renders
 * numeric + warning glyph, never colour alone), and the 黃單 capture log.
 *
 * Labels ALWAYS come from `ofcVocabulary.ts` via useCheckpointLabel —
 * bare A-codes never reach UI copy.
 */

import { Button, Card, Steps, Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { CheckOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import TableContainer, { renderDash } from '@/components/common/TableContainer';
import { SectionHeader } from '@/components/products/ProductDetailPage/sections/SectionHeader';
import { TimestampDisplay } from '@/components/common/TimestampDisplay';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import type { OccLine } from '@/types/api/occ';
import type { PurchaseOrder, PurchaseOrderLine } from '@/types/api/purchaseOrder';
import type { VendorCheckpointRecord } from '@/stores/poStore';
import {
  CHECKPOINT_BUCKET,
  CHECKPOINT_CODES,
  deriveCheckpointStatuses,
  occAllocationForPoLine,
  useCheckpointLabel,
} from '../checkpoints';

const { Text } = Typography;

interface CheckpointsSectionProps {
  po: PurchaseOrder;
  occLines: OccLine[];
  records: VendorCheckpointRecord[];
  /** Status gate resolved by the page (sent/vendor_confirmed/shipped). */
  showConfirmCheckpoint: boolean;
  onOpenConfirm: () => void;
}

/** Bucket cell: number always; short bucket adds the warning glyph. */
const BucketCell = ({
  value,
  baseline,
  warningColor,
}: {
  value: number;
  baseline: number;
  warningColor: string;
}) => {
  if (value <= 0) return <>{renderDash()}</>;
  const short = value < baseline;
  return (
    <span
      className="tw-inline-flex tw-items-center tw-gap-1"
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {short && <WarningOutlined style={{ color: warningColor }} />}
      {value}
    </span>
  );
};

export const CheckpointsSection = ({
  po,
  occLines,
  records,
  showConfirmCheckpoint,
  onOpenConfirm,
}: CheckpointsSectionProps) => {
  const { t } = useTranslation(['procurement', 'common']);
  const { token } = theme.useToken();
  const cpLabel = useCheckpointLabel();

  const statuses = deriveCheckpointStatuses(po, occLines, records);
  const firstOpen = statuses.findIndex((status) => !status.recorded);
  const currentStep = firstOpen === -1 ? statuses.length : firstOpen;

  const bucketColumns: TableProps<PurchaseOrderLine>['columns'] = [
    {
      title: t('procurement:detail.checkpoints.bucketLine'),
      key: 'line',
      width: 220,
      render: (_: unknown, line) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>{line.sku}</span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {line.productName}
          </span>
        </div>
      ),
    },
    {
      title: t('procurement:detail.checkpoints.ordered'),
      key: 'ordered',
      width: 90,
      align: 'right',
      render: (_: unknown, line) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{line.qty}</span>
      ),
    },
    ...CHECKPOINT_CODES.map((code) => ({
      title: cpLabel(code),
      key: code,
      width: 150,
      align: 'right' as const,
      render: (_: unknown, line: PurchaseOrderLine) => {
        const alloc = occAllocationForPoLine(occLines, line.id);
        if (!alloc) return renderDash();
        return (
          <BucketCell
            value={alloc[CHECKPOINT_BUCKET[code]]}
            baseline={line.qty}
            warningColor={token.colorWarning}
          />
        );
      },
    })),
    {
      title: t('procurement:detail.checkpoints.variance'),
      key: 'variance',
      width: 130,
      align: 'right',
      render: (_: unknown, line) => {
        const alloc = occAllocationForPoLine(occLines, line.id);
        if (!alloc) return renderDash();
        // Latest recorded bucket wins (A3 → A2 → A1); variance vs ordered.
        const latest = alloc.qtyLoaded || alloc.qtyNoticeConfirmed || alloc.qtyEstimated;
        if (!latest) return renderDash();
        const variance = latest - line.qty;
        if (variance === 0) return <span style={{ fontVariantNumeric: 'tabular-nums' }}>0</span>;
        return (
          <span
            className="tw-inline-flex tw-items-center tw-gap-1"
            style={{
              fontVariantNumeric: 'tabular-nums',
              color: variance < 0 ? token.colorError : token.colorWarning,
            }}
          >
            <WarningOutlined />
            {variance > 0 ? `+${variance}` : variance}
          </span>
        );
      },
    },
  ];

  const recordLineColumns: TableProps<VendorCheckpointRecord['lines'][number]>['columns'] = [
    {
      title: t('procurement:detail.checkpoints.bucketLine'),
      dataIndex: 'sku',
      key: 'sku',
      width: 180,
      render: (sku: string) => <span style={{ fontWeight: token.fontWeightStrong }}>{sku}</span>,
    },
    {
      title: t('procurement:confirmDrawer.qtyConfirmed'),
      key: 'qty',
      width: 130,
      align: 'right',
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {row.qtyConfirmed}/{row.qtyBaseline}
        </span>
      ),
    },
    {
      title: t('procurement:detail.checkpoints.deliveryDate'),
      dataIndex: 'deliveryDate',
      key: 'deliveryDate',
      width: 130,
      render: (value: string | null) => value || renderDash(),
    },
    {
      title: t('procurement:detail.checkpoints.priceAdjusted'),
      dataIndex: 'priceAdjusted',
      key: 'priceAdjusted',
      width: 110,
      render: (value: boolean) =>
        value ? <CheckOutlined style={{ color: token.colorWarning }} /> : renderDash(),
    },
    {
      title: t('procurement:detail.checkpoints.boxCount'),
      dataIndex: 'boxCount',
      key: 'boxCount',
      width: 90,
      align: 'right',
      render: (value: number | null) =>
        value != null ? (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        ) : (
          renderDash()
        ),
    },
    {
      title: t('procurement:detail.checkpoints.packaging'),
      dataIndex: 'packaging',
      key: 'packaging',
      render: (value: string | null) => value || renderDash(),
    },
    {
      title: t('procurement:fields.status'),
      key: 'exception',
      width: 190,
      render: (_: unknown, row) =>
        row.exception ? (
          <Tag icon={<WarningOutlined />} color="orange" style={{ margin: 0 }}>
            {t('procurement:detail.checkpoints.exceptionRaised', {
              owner: t(`procurement:owner.${row.exception.owner}`),
            })}
          </Tag>
        ) : (
          renderDash()
        ),
    },
  ];

  return (
    <div>
      <SectionHeader
        title={t('procurement:detail.checkpoints.title')}
        subtitle={t('procurement:detail.checkpoints.subtitle')}
        extra={
          showConfirmCheckpoint ? (
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={onOpenConfirm}
              style={{
                height: `${controlHeightTokens.controlHeightXL}px`,
                borderRadius: radiusTokens.card,
              }}
            >
              {t('procurement:actions.confirmCheckpoint')}
            </Button>
          ) : undefined
        }
      />

      {/* Three-checkpoint timeline */}
      <Card
        className="tw-mb-6"
        style={{
          borderRadius: radiusTokens.panel,
          border: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Steps
          current={currentStep}
          items={statuses.map((status) => ({
            title: cpLabel(status.code),
            description: status.recorded
              ? status.at
                ? t('procurement:detail.checkpoints.recordedAt', {
                    date: status.at.slice(0, 10),
                  })
                : t('procurement:detail.checkpoints.recordedAt', { date: '-' })
              : t('procurement:detail.checkpoints.notRecorded'),
          }))}
        />
        {po.status === 'draft' && (
          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginMD, fontSize: token.fontSizeSM }}
          >
            {t('procurement:detail.checkpoints.sendFirst')}
          </Text>
        )}
      </Card>

      {/* Per-line confirmed buckets (OCC projection read) */}
      <TableContainer className="tw-mb-6">
        <Table<PurchaseOrderLine>
          rowKey="id"
          size="small"
          dataSource={po.lines}
          columns={bucketColumns}
          pagination={false}
          scroll={{ x: 1000 }}
        />
      </TableContainer>

      {/* 黃單 capture log */}
      <SectionHeader title={t('procurement:detail.checkpoints.yellowSlip')} />
      {records.length === 0 ? (
        <Text type="secondary">{t('procurement:detail.checkpoints.logEmpty')}</Text>
      ) : (
        records.map((record) => (
          <Card
            key={record.id}
            className="tw-mb-4"
            style={{
              borderRadius: radiusTokens.panel,
              border: `1px solid ${token.colorBorderSecondary}`,
            }}
            title={
              <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                <span>{cpLabel(record.checkpoint)}</span>
                <TimestampDisplay date={record.at} showLabel={false} />
              </div>
            }
          >
            <Table<VendorCheckpointRecord['lines'][number]>
              rowKey="poLineId"
              size="small"
              dataSource={record.lines}
              columns={recordLineColumns}
              pagination={false}
              scroll={{ x: 1020 }}
            />
            {record.note && (
              <Text
                type="secondary"
                style={{ display: 'block', marginTop: token.marginSM, fontSize: token.fontSizeSM }}
              >
                {t('procurement:detail.checkpoints.noteLabel')}: {record.note}
              </Text>
            )}
          </Card>
        ))
      )}
    </div>
  );
};

export default CheckpointsSection;
